import { useEffect } from "react";

/**
 * useSeo — per-route SEO helper.
 * Mutates document.title, meta tags, canonical link, and injects/removes JSON-LD.
 * Lighter than react-helmet-async; no provider/context needed.
 */
export function useSeo({ title, description, canonical, image, jsonLd, keywords } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    const prevDescEl = document.querySelector('meta[name="description"]');
    const prevDesc = prevDescEl?.getAttribute("content");

    if (title) document.title = title;

    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [attrName, attrVal] = selector.replace(/^meta\[/, "").replace(/\]$/, "").split("=");
        el.setAttribute(attrName, attrVal.replace(/['"]/g, ""));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    if (description) {
      setMeta('meta[name="description"]', "content", description);
      setMeta('meta[property="og:description"]', "content", description);
      setMeta('meta[name="twitter:description"]', "content", description);
    }
    if (title) {
      setMeta('meta[property="og:title"]', "content", title);
      setMeta('meta[name="twitter:title"]', "content", title);
    }
    if (image) {
      setMeta('meta[property="og:image"]', "content", image);
      setMeta('meta[name="twitter:image"]', "content", image);
    }
    if (keywords) setMeta('meta[name="keywords"]', "content", keywords);

    // Canonical
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
      setMeta('meta[property="og:url"]', "content", canonical);
    }

    // JSON-LD (per-page, additive — uses a unique data-* attribute)
    let script;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-page-jsonld", "1");
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      document.title = prevTitle;
      if (prevDescEl && prevDesc) prevDescEl.setAttribute("content", prevDesc);
      if (script && script.parentNode) script.parentNode.removeChild(script);
    };
  }, [title, description, canonical, image, keywords, JSON.stringify(jsonLd)]);
}
